import Stack from '@mui/material/Stack';
import { createElement } from 'react';
import { generatePath } from 'react-router';
import type { SubmitHandler, UseFormReturn, UseFormSetValue } from 'react-hook-form';
import { toast } from 'react-toastify';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import {
  BookingModeDto,
  CreateProcessRequestDto,
  ContactDto,
  ContactItemDto,
  CustomerPropertiesDto,
  CustomerItemDto,
  CustomerStatusDto,
  CustomerTypeDto,
  ProcessCreationDefaultDataMatchDto,
  ProcessCreationResponseDto,
  ResourceIdDto,
  ResourceNoteCreationRequestDto,
  ResourceTypeDto,
} from '@/api/generated';
import { ToastConfig } from '@/config/ToastConfig.util.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { customerStatusUtil } from '@/views/customers/status/customerStatus.util.ts';
import { ProcessCalendarEntryQueryFormModel } from '@/views/processes/calendarEntryQuery/model/ProcessCalendarEntryQueryForm.model.ts';
import { processCalendarEntryQueryUtil } from '@/views/processes/calendarEntryQuery/utils/processCalendarEntryQuery.util.ts';
import { CreateProcessFormModel } from '@/views/processesCreate/model/CreateProcessForm.model.ts';
import { ProcessCreateDialogRequest } from '@/views/processesCreate/model/ProcessCreateDialog.model.ts';

type CustomersApi = ReturnType<typeof useApiClient>['customersApi'];

type SubmitParams = {
  addResourceNote: (
    request: ResourceNoteCreationRequestDto & { resource: ResourceIdDto },
  ) => Promise<unknown>;
  bookingMode: BookingModeDto;
  /** The user may open the offer screen, so the toast links the created offer. */
  canOpenOffer: boolean;
  /** The user may open the process screen, so the toast links the created process. */
  canOpenProcess: boolean;
  createProcess: (request: CreateProcessRequestDto) => Promise<ProcessCreationResponseDto>;
  data: CreateProcessFormModel;
  /** The offer section is ready, so the chosen offer definition and item are sent. */
  includeOffer: boolean;
  onCreated: () => void;
  t: (key: 'toast.created' | 'toast.open' | 'toast.openOffer') => string;
  userId: string;
};

type SubmitHandlerParams = Omit<SubmitParams, 'data'> & {
  onInvalidContact: () => void;
};

export const processesCreateUtil = {
  resetForm: (
    formMethods: UseFormReturn<CreateProcessFormModel>,
    request: ProcessCreateDialogRequest,
    userSelectOption: SelectSearchOptionModel,
    calendarEntryQuery: ProcessCalendarEntryQueryFormModel,
  ): void => {
    formMethods.reset({
      ...calendarEntryQuery,
      contact: request.contactId
        ? { contactId: request.contactId, customerId: request.customerId }
        : null,
      customer: request.customerId ? { customerId: request.customerId } : null,
      emailThreadId: request.emailThreadId,
      name: '',
      noteText: '',
      offerBookingTarget: { definition: null, item: null },
      processOwnerId: userSelectOption,
    });
  },
  synchronizeFormDetails: (
    formMethods: UseFormReturn<CreateProcessFormModel>,
    contact: ContactDto['basicContact'] | undefined,
    customer: CustomerPropertiesDto | undefined,
  ): void => {
    if (contact && formMethods.getValues('contact')?.contactId === contact.contactId) {
      formMethods.setValue('contact', contact);
    }
    if (!customer) return;
    const selectedCustomerId = formMethods.getValues('customer')?.customerId;
    const selectedContactCustomerId = formMethods.getValues('contact')?.customerId;
    if (
      customer.customerId === selectedCustomerId ||
      customer.customerId === selectedContactCustomerId
    ) {
      formMethods.setValue('customer', customer);
    }
  },
  /** Applies the default match (see `getDefaultMatch`) to the fields the user has not changed. */
  applyDefaultMatch: ({
    activeMatch,
    isContactDirty,
    isCustomerDirty,
    isProcessOwnerDirty,
    matchOwnerOption,
    selectedMatch,
    setValue,
  }: {
    activeMatch: ProcessCreationDefaultDataMatchDto | null;
    isContactDirty: boolean;
    isCustomerDirty: boolean;
    isProcessOwnerDirty: boolean;
    matchOwnerOption?: SelectSearchOptionModel;
    selectedMatch: ProcessCreationDefaultDataMatchDto | null;
    setValue: UseFormSetValue<CreateProcessFormModel>;
  }): void => {
    if (!activeMatch || !matchOwnerOption || selectedMatch) return;
    if (!isContactDirty && !isCustomerDirty) {
      setValue('contact', {
        contactId: activeMatch.contactId,
        customerId: activeMatch.customerId,
      });
    }
    if (!isCustomerDirty) setValue('customer', { customerId: activeMatch.customerId });
    if (!isProcessOwnerDirty) setValue('processOwnerId', matchOwnerOption);
  },
  selectMatch: (
    setValue: UseFormSetValue<CreateProcessFormModel>,
    setSelectedMatch: (match: ProcessCreationDefaultDataMatchDto) => void,
    usersOptions: SelectSearchOptionModel[],
    match: ProcessCreationDefaultDataMatchDto,
  ): void => {
    setSelectedMatch(match);
    setValue('contact', { contactId: match.contactId, customerId: match.customerId });
    setValue('customer', { customerId: match.customerId });
    setValue('processOwnerId', processesCreateUtil.getMatchOwnerOption(usersOptions, match));
  },
  selectContact: (
    setValue: UseFormSetValue<CreateProcessFormModel>,
    contact: ContactItemDto,
  ): void => {
    setValue('contact', contact, { shouldDirty: true });
    setValue(
      'customer',
      contact.customer
        ? {
            customerId: contact.customer.customerId,
            name: contact.customer.name,
            type: contact.customer.type,
          }
        : null,
      { shouldDirty: true },
    );
  },
  getMatchOwnerOption: (
    usersOptions: SelectSearchOptionModel[],
    match: ProcessCreationDefaultDataMatchDto,
  ): SelectSearchOptionModel =>
    usersOptions.find(user => user.value === match.ownerId) ?? {
      label: `${match.ownerFirstName} ${match.ownerLastName}`.trim(),
      value: match.ownerId,
    },
  getCustomerDetails: async (
    customersApi: CustomersApi,
    customerId?: number,
    customerType?: CustomerTypeDto,
  ): Promise<CustomerPropertiesDto | undefined> => {
    if (!customerId) return undefined;
    const type =
      customerType ?? (await processesCreateUtil.getCustomerType(customersApi, customerId));
    if (type === CustomerTypeDto.Individual) {
      return (await customersApi.getIndividualCustomer(customerId)).data;
    }
    if (type === CustomerTypeDto.ForeignBusiness) {
      return (await customersApi.getForeignBusinessCustomer(customerId)).data;
    }
    return (await customersApi.getBusinessCustomer(customerId)).data;
  },
  getCustomerType: async (
    customersApi: CustomersApi,
    customerId?: number,
  ): Promise<CustomerTypeDto> => {
    if (!customerId) throw new Error('Customer ID is required');
    return (await customersApi.getCustomerType(customerId)).data.type;
  },
  /**
   * The match applied by default: the first one whose customer can become the customer of a process, so a
   * matched Prospekt is skipped; none until the statuses of the matched customers are known.
   */
  getDefaultMatch: (
    matches: ProcessCreationDefaultDataMatchDto[],
    customerStatuses: Partial<Record<number, CustomerStatusDto>>,
    isLoadingStatuses: boolean,
  ): ProcessCreationDefaultDataMatchDto | null => {
    if (isLoadingStatuses) return null;
    return (
      matches.find(match => processesCreateUtil.isMatchSelectable(match, customerStatuses)) ?? null
    );
  },
  /** A match can be chosen unless its customer is known not to be ACTIVE (Prospekt, Odrzucony, inactive). */
  isMatchSelectable: (
    match: ProcessCreationDefaultDataMatchDto,
    customerStatuses: Partial<Record<number, CustomerStatusDto>>,
  ): boolean => customerStatusUtil.canBeProcessCustomer(customerStatuses[match.customerId]),
  selectCustomer: (
    setValue: UseFormSetValue<CreateProcessFormModel>,
    customer: CustomerItemDto,
    ): void => {
    setValue('contact', null, { shouldDirty: true });
    setValue(
      'customer',
      {
        customerId: customer.customerId,
        flag: customer.flag,
        name: customer.name,
        nip: customer.nip,
        status: customer.status,
        type: customer.type,
      },
      { shouldDirty: true },
    );
  },
  submitProcessCreateForm: async ({
    addResourceNote,
    bookingMode,
    canOpenOffer,
    canOpenProcess,
    createProcess,
    data,
    includeOffer,
    onCreated,
    t,
    userId,
  }: SubmitParams): Promise<boolean> => {
    const {
      contact,
      customer,
      noteText,
      offerBookingTarget,
      processOwnerId,
      requestedDictionaryId,
      requestedDictionaryType,
      requestedFrom,
      requestedTo,
      ...requestDto
    } = data;
    const customerId = customer?.customerId ?? contact?.customerId;
    if (contact && !customerId) return false;

    const response = await createProcess({
      ...requestDto,
      ...processCalendarEntryQueryUtil.toRequest(
        { requestedDictionaryId, requestedDictionaryType, requestedFrom, requestedTo },
        bookingMode,
      ),
      contactId: contact?.contactId,
      customerId,
      ...(includeOffer && {
        offerComponentDefinitionItemId: offerBookingTarget.item?.value,
        offerDefinitionId: offerBookingTarget.definition?.value,
      }),
      processCreatorId: userId,
      processOwnerId: processOwnerId.value,
    });
    onCreated();
    const { offerInstanceId, processId } = response;
    toast(
      createElement(
        Stack,
        { direction: 'row', spacing: 1 },
        createElement('span', null, t('toast.created')),
        canOpenProcess
          ? createElement(
              'a',
              { href: generatePath(RouteEnum.PROCESSES_DETAILS, { processId }) },
              t('toast.open'),
            )
          : null,
        offerInstanceId && canOpenOffer
          ? createElement(
              'a',
              {
                href: generatePath(RouteEnum.PROCESSES_DETAILS_OFFER_INSTANCE, {
                  offerInstanceId: String(offerInstanceId),
                  processId,
                }),
              },
              t('toast.openOffer'),
            )
          : null,
      ),
      ToastConfig.success,
    );

    if (noteText) {
      await addResourceNote({
        creatorId: userId,
        resource: { resourceId: processId, type: ResourceTypeDto.Process },
        text: noteText,
      });
    }
    return true;
  },
  createSubmitHandler: (params: SubmitHandlerParams): SubmitHandler<CreateProcessFormModel> =>
    async data => {
      const { onInvalidContact, ...submitParams } = params;
      // a refused save (e.g. 409 CUSTOMER_NOT_APPROVED) is reported by the toast of its mutation
      const succeeded = await processesCreateUtil
        .submitProcessCreateForm({ ...submitParams, data })
        .catch(() => undefined);
      if (succeeded === false) onInvalidContact();
    },
};
